import styled from '@emotion/styled';

import type { IconName } from 'storybookConfig/mixins/icons';
import materialSymbol from 'storybookConfig/mixins/materialSymbol';

const SIZES = {
  medium: { tile: '48px', icon: '24px' },
  large: { tile: '80px', icon: '40px' },
} as const;

export interface IconTileProps {
  /**
   * A Lucide name from `mixins/icons.ts`
   */
  name: IconName;
  /**
   * `medium` (48px) beside a panel's text, `large` (80px) above an empty state's title
   */
  size?: keyof typeof SIZES;
}

const Tile = styled.span<{ $name: IconName; $size: keyof typeof SIZES }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => SIZES[$size].tile};
  height: ${({ $size }) => SIZES[$size].tile};
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.color.accentSubtle};

  ${({ $name, $size }) =>
    materialSymbol({ name: $name, color: 'accent', size: SIZES[$size].icon })};
`;

/**
 * The icon that heads an empty state, a panel or a choice: a Lucide icon in iris on an iris-tint
 * circle. Decorative, so the text beside it names what it shows.
 */
const IconTile = ({ name, size = 'large' }: IconTileProps) => (
  <Tile $name={name} $size={size} aria-hidden="true" />
);

export default IconTile;
